// MACHINERY V2 (Stroje) — V3 table columns + client-side sort (M2.13).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §8 M2.13. Reference impl:
// design_handoff_v3v4v5_mobile/desktop/machinery-v3-table.jsx (SortableTh) +
// desktop/README.md §V3.
//
// ONE TABLE, consumed by the header AND by the sorter, so a column can never be
// offered as sortable in the UI but unhandled by the comparator (or vice versa).
// Same discipline as fleetGroupBy.ts and fleetStatusFilters.ts.
//
// >>> SORTING IS CLIENT-SIDE OVER THE FULL SET. <<< The fleet endpoint has no
// pagination and useFleetPageData already holds every machine in the scope (see
// its header: the client holds the complete set, which is what makes the facet
// counts honest). So a sort is a pure reordering of what is already in memory —
// no fetch, no query param, consistent with the established client-side
// faceting.
//
// >>> STATUS AND CONNECTIVITY SORT THROUGH THE CONTRACT, NEVER RAW TELEMETRY.
// The status key is the machine's position in CARD_STATE_ORDER — the canonical
// 9-state order the rail and the rules grid already use — and the connectivity
// key is `deriveCardPresentation().dotConn`, the SAME derived value the dot
// paints. Nothing here tests `alarms.length`, `status === "alarm"` or a raw
// `conn`, so a column cannot order rows by a state different from the one they
// display.
//
// TWO COLUMNS ARE DELIBERATELY NOT SORTABLE (and render no sort affordance):
//   · AKTUÁLNE — its content IS the status (fleetTableCells renders the
//     deriver's branches), so a sort here would be a second, differently-named
//     control for the STAV ordering.
//   · 30 DNÍ — data-gated (§6 Layer-2 rollup, see FleetTableRow). A control
//     that orders rows by a column with no data is a lie about the data, and
//     the artboard's ↕ glyph on it assumed the rollup existed.
// The artboard draws a glyph on both; dropping it is a knowing deviation,
// recorded here rather than silently.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import { Machine } from "../../../models/models";
import { getMachineDisplayName } from "../../../../../shared/utils/machineUtils";
import {
  CARD_STATE_ORDER,
  MachineryV2CardState,
  MachineryV2Conn,
  MachineryV2Telemetry,
  deriveCardPresentation,
  unconnectedTelemetry,
} from "../cardStates";

export type FleetTableSortColumn = "status" | "conn" | "machine" | "location" | "openSr";
export type FleetTableSortDirection = "asc" | "desc";

export interface FleetTableSort {
  column: FleetTableSortColumn;
  direction: FleetTableSortDirection;
}

/** The artboard's sort-active column is STROJ, ascending (↑ in --mod-accent). */
export const DEFAULT_FLEET_TABLE_SORT: FleetTableSort = {
  column: "machine",
  direction: "asc",
};

export interface FleetTableColumnSpec {
  key: string;
  /** i18next key (myMachinery namespace); the sheet renders it uppercase. */
  labelKey: string;
  /** Fixed px width; undefined = the flex column (STROJ takes the remainder). */
  width?: number;
  align?: "left" | "center" | "right";
  /** null = header renders with no sort affordance (see the header note). */
  sort: FleetTableSortColumn | null;
}

/** Column order + widths verbatim from README §V3 / the M2.13 ticket row. */
export const FLEET_TABLE_COLUMNS: readonly FleetTableColumnSpec[] = [
  { key: "status", labelKey: "myMachinery:v2.fleet.table.colStatus", width: 130, sort: "status" },
  { key: "conn", labelKey: "myMachinery:v2.fleet.table.colConn", width: 38, align: "center", sort: "conn" },
  { key: "machine", labelKey: "myMachinery:v2.fleet.table.colMachine", sort: "machine" },
  { key: "location", labelKey: "myMachinery:v2.fleet.table.colLocation", width: 170, sort: "location" },
  { key: "current", labelKey: "myMachinery:v2.fleet.table.colCurrent", width: 250, sort: null },
  { key: "days30", labelKey: "myMachinery:v2.fleet.table.col30Days", width: 120, sort: null },
  { key: "openSr", labelKey: "myMachinery:v2.fleet.table.colOpenSr", width: 80, align: "right", sort: "openSr" },
];

/** Width of the trailing kebab column (no label, no sort). */
export const FLEET_TABLE_KEBAB_WIDTH = 36;

/** Sum of the fixed widths + the kebab + a floor for the flex STROJ column.
 *  The table CARD scrolls horizontally below this — never the page (§ "no
 *  horizontal page scroll"): see FleetTable.tsx. */
export const FLEET_TABLE_MIN_WIDTH = 1000;

/** Click a header: same column flips direction, a new column starts ascending. */
export const nextSort = (
  prev: FleetTableSort,
  column: FleetTableSortColumn
): FleetTableSort =>
  prev.column === column
    ? { column, direction: prev.direction === "asc" ? "desc" : "asc" }
    : { column, direction: "asc" };

/** Connectivity rank for the PRIPOJ. column: most trustworthy first. Reads the
 *  DERIVED dot state, so an `offline` machine ranks with the long-stale ones
 *  exactly as its dot paints (dotConnFor in cardStates.ts). Takes the dot
 *  itself rather than the telemetry so the caller derives the presentation ONCE
 *  and reads both the rank and the `missing` flag off the same object. */
const connRank = (dot: MachineryV2Conn | null): number => {
  if (dot === "live") return 0;
  if (dot === "stale") return 1;
  if (dot === "longstale") return 2;
  return 3; // unconnected / awaiting — no dot at all
};

const locationLabel = (m: Machine): string => {
  if (!m.locationName) return "";
  return m.parentLocationName ? m.parentLocationName + " " + m.locationName : m.locationName;
};

interface SortKey {
  /** Rows with nothing in the sorted column go LAST in BOTH directions — a
   *  reversed sort should not open with a screen of blanks. */
  missing: boolean;
  num: number | null;
  text: string | null;
}

const sortKey = (
  m: Machine,
  column: FleetTableSortColumn,
  states: Map<string, MachineryV2CardState>,
  telemetry: Map<string, MachineryV2Telemetry>
): SortKey => {
  // `missing` TRACKS WHAT THE CELL RENDERS, not the position in the order.
  // Both branches below used to under-report it: `unconnected` is
  // CARD_STATE_ORDER[0], so `idx < 0` scored it present, and the conn branch
  // hardcoded false. Both cells paint a bare dash for those rows, and the fleet
  // is overwhelmingly unconnected — so STAV-ascending and PRIPOJ.-descending
  // each opened with a full screen of blanks, precisely what SortKey.missing
  // promises above cannot happen. Read from deriveCardPresentation(), the same
  // contract the cells read, so the flag cannot drift from the rendering again.
  if (column === "status") {
    const state = states.get(m.id);
    const idx = state ? CARD_STATE_ORDER.indexOf(state) : -1;
    const p = deriveCardPresentation(telemetry.get(m.id) || unconnectedTelemetry());
    // THE SAME PREDICATE StatusCell USES, NEGATED — not a paraphrase of it.
    // StatusCell paints a chip on
    //   `p.showStatusRow && (p.showAwaitingBadge || p.badgeStatus !== null)`
    // (fleetTableCells.tsx:98, personally read at staging time) and a dash
    // otherwise, so the dash branch is exactly the De Morgan below.
    //
    // `!showStatusRow` ALONE MISSED THE CASE M2.13's OWN DASH FALLBACK ADDED: a
    // `stale` machine has showStatusRow TRUE but carries no status to paint
    // ("TelemetryStale" maps to `{ conn: "stale", status: null }`,
    // fleetTelemetry.ts:100, re-read at staging time), so StatusBadge returns
    // null and the cell renders the dash — while this key scored it `missing:
    // false` and sorted it as though it had content. That is the exact defect
    // class the flag exists to prevent, surviving in a narrower case.
    //
    // NOT a new ordering policy: SortKey.missing above already promises
    // "rows with nothing in the sorted column go LAST in BOTH directions".
    // This makes that promise true in one more case.
    const rendersNothing =
      !p.showStatusRow || (!p.showAwaitingBadge && p.badgeStatus === null);
    return { missing: idx < 0 || rendersNothing, num: idx, text: null };
  }
  if (column === "conn") {
    // ConnCell renders a Dash whenever dotConn is null (unconnected + awaiting).
    const dot = deriveCardPresentation(telemetry.get(m.id) || unconnectedTelemetry()).dotConn;
    return { missing: dot === null, num: connRank(dot), text: null };
  }
  if (column === "openSr") {
    return { missing: false, num: m.openServiceReportsCount || 0, text: null };
  }
  const text = column === "machine" ? getMachineDisplayName(m) : locationLabel(m);
  return { missing: !text, num: null, text };
};

/**
 * A STABLE, non-mutating sort of one section's machines.
 *
 * Stability matters: `sections` is memoised upstream, and an unstable sort would
 * let two machines with equal keys swap places on every 60 s telemetry tick.
 * Array.prototype.sort is spec-stable since ES2019, and the input is copied
 * first so the memoised section array is never reordered in place.
 */
export const sortMachines = (
  machines: Machine[],
  sort: FleetTableSort,
  states: Map<string, MachineryV2CardState>,
  telemetry: Map<string, MachineryV2Telemetry>,
  lang: string
): Machine[] => {
  const dir = sort.direction === "asc" ? 1 : -1;
  return [...machines].sort((a, b) => {
    const ka = sortKey(a, sort.column, states, telemetry);
    const kb = sortKey(b, sort.column, states, telemetry);
    if (ka.missing !== kb.missing) return ka.missing ? 1 : -1;
    if (ka.num !== null && kb.num !== null) return (ka.num - kb.num) * dir;
    return (ka.text || "").localeCompare(kb.text || "", lang) * dir;
  });
};
